/**
 * ViewerJsonPanel Component
 * Displays the full map JSON data in viewer mode
 * Supports both graph instance (Editor) and floor data (Viewer)
 */

import { useMemo, useState, useEffect } from 'react'
import { dia } from '@joint/core'
import { Floor } from '../../../map-editor-shared/store/floorStore'
import styles from './ViewerJsonPanel.module.css'

interface ViewerJsonPanelProps {
  graph?: dia.Graph | null
  projectName?: string
  floors?: Floor[]  // For viewer mode - pass floor data directly
}

// Extract objects from graph cells (stored graphJson format)
function extractObjectsFromCells(cells: any[]): any[] {
  if (!cells || !Array.isArray(cells)) return []

  return cells
    .filter(cell => cell.type !== 'standard.Link')  // Exclude links
    .map(cell => ({
      id: String(cell.id),
      type: cell.objectType || cell.type || 'Unknown',
      name: cell.name || cell.objectType || 'Unnamed',
      geometry: {
        x: cell.position?.x || 0,
        y: cell.position?.y || 0,
        width: cell.size?.width || 0,
        height: cell.size?.height || 0,
        rotation: cell.angle || 0,
      },
      style: {
        fill: cell.attrs?.body?.fill || '#ffffff',
        stroke: cell.attrs?.body?.stroke || '#000000',
        strokeWidth: cell.attrs?.body?.strokeWidth || 1,
      },
      properties: cell.properties || {},
      relations: cell.relations || [],
    }))
}

export function ViewerJsonPanel({ graph, projectName, floors }: ViewerJsonPanelProps) {
  // Track graph changes to force re-render
  const [, setGraphVersion] = useState(0)

  // Listen to graph changes
  useEffect(() => {
    if (!graph) return

    const handleChange = () => {
      setGraphVersion(v => v + 1)
    }

    // Listen to all graph change events
    graph.on('add remove change', handleChange)

    return () => {
      graph.off('add remove change', handleChange)
    }
  }, [graph])

  // Generate JSON data from graph OR floors data
  const jsonData = useMemo(() => {
    // Priority 1: Use floors data if provided (Viewer mode)
    if (floors && floors.length > 0) {
      const allObjects: any[] = []

      floors.forEach(floor => {
        const cells = floor.mapData?.graphJson?.cells || []
        const floorObjects = extractObjectsFromCells(cells)

        // Add floor info to each object
        floorObjects.forEach(obj => {
          allObjects.push({
            ...obj,
            floor: floor.name,
            floorId: floor.id,
          })
        })
      })

      return {
        metadata: {
          version: '1.0.0',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          projectName: projectName || 'Unknown Project',
          totalFloors: floors.length,
        },
        objects: allObjects,
      }
    }

    // Priority 2: Use graph instance if available (Editor mode)
    if (graph) {
      const elements = graph.getElements()
      const objects = elements.map((element) => {
        const position = element.position()
        const size = element.size()
        const attrs = element.attributes

        return {
          id: String(element.id),
          type: attrs.objectType || attrs.type || 'Unknown',
          name: attrs.name || attrs.objectType || 'Unnamed',
          geometry: {
            x: position.x,
            y: position.y,
            width: size.width,
            height: size.height,
            rotation: attrs.angle || 0,
          },
          style: {
            fill: attrs.attrs?.body?.fill || '#ffffff',
            stroke: attrs.attrs?.body?.stroke || '#000000',
            strokeWidth: attrs.attrs?.body?.strokeWidth || 1,
          },
          properties: attrs.properties || {},
          relations: attrs.relations || [],
        }
      })

      return {
        metadata: {
          version: '1.0.0',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          projectName: projectName || 'Unknown Project',
        },
        objects,
      }
    }

    // Fallback: Empty data
    return {
      metadata: {
        version: '1.0.0',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        projectName: projectName || 'Unknown Project',
      },
      objects: [],
    }
  }, [graph, projectName, floors, graph?.getCells().length])

  // Format JSON with syntax highlighting
  const formattedJson = useMemo(() => {
    return JSON.stringify(jsonData, null, 2)
  }, [jsonData])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>맵 JSON 데이터</h3>
        <div className={styles.info}>
          총 {jsonData.objects.length}개 객체
        </div>
      </div>

      <div className={styles.content}>
        <pre className={styles.jsonDisplay}>
          <code>{formattedJson}</code>
        </pre>
      </div>

      <div className={styles.actions}>
        <button
          className={styles.copyButton}
          onClick={() => {
            navigator.clipboard.writeText(formattedJson)
            alert('JSON 데이터가 클립보드에 복사되었습니다.')
          }}
        >
          📋 복사
        </button>
        <button
          className={styles.downloadButton}
          onClick={() => {
            const blob = new Blob([formattedJson], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${projectName}-map.json`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
          }}
        >
          💾 다운로드
        </button>
      </div>
    </div>
  )
}
